import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageCircle, AlertTriangle, CheckCircle2, Loader2, Eye, EyeOff, SendHorizonal } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { api, apiError } from "../../lib/api";

const CAMPAIGN_KEYS = [
  { key: "booking_confirmed", label: "Booking confirmed" },
  { key: "booking_cancelled", label: "Booking cancelled" },
  { key: "rescheduled", label: "Rescheduled" },
  { key: "reminder_24h", label: "24h reminder" },
  { key: "payment_succeeded", label: "Payment succeeded" },
];

export default function HospitalIntegrations() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({
    api_key: "", is_enabled: false,
    campaign_map: Object.fromEntries(CAMPAIGN_KEYS.map((c) => [c.key, ""])),
  });
  const [test, setTest] = useState({
    to_phone: "", campaign_name: CAMPAIGN_KEYS[0].key, param1: "", param2: "",
  });
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = () =>
    api.get("/hospital/integrations/whatsapp").then((r) => {
      setData(r.data);
      setForm({
        api_key: "",
        is_enabled: !!r.data.is_enabled,
        campaign_map: { ...form.campaign_map, ...(r.data.campaign_map || {}) },
      });
    });

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/hospital/integrations/whatsapp", form);
      toast.success("WhatsApp settings saved");
      await load();
    } catch (e) {
      toast.error(apiError(e, "Save failed"));
    } finally { setSaving(false); }
  };

  const runTest = async () => {
    if (!test.to_phone.startsWith("+")) {
      toast.error("Phone must be in E.164 format (e.g. +911234567890)");
      return;
    }
    setTesting(true);
    try {
      const params = [test.param1, test.param2].filter((p) => p !== "");
      const campaign = form.campaign_map[test.campaign_name] || "";
      if (!campaign) {
        toast.error("That notification type has no campaign name configured. Save it first.");
        setTesting(false);
        return;
      }
      const r = await api.post("/hospital/integrations/whatsapp/test", {
        to_phone: test.to_phone,
        campaign_name: campaign,
        template_params: params,
      });
      toast.success("AiSensy accepted the request");
      // show a short response preview in the console for diagnostics
      // eslint-disable-next-line no-console
      console.log("[whatsapp/test response]", r.data);
    } catch (e) {
      toast.error(apiError(e, "AiSensy rejected the call"));
    } finally { setTesting(false); }
  };

  if (!data) return <div className="text-sm text-mb-fg2">Loading…</div>;
  const ok = data.is_enabled && data.is_configured;

  return (
    <div className="space-y-8 max-w-4xl" data-testid="hospital-integrations">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-teal-400">◖ whatsapp</div>
        <h1 className="mt-2 text-3xl font-light tracking-tight">WhatsApp notifications</h1>
        <p className="mt-2 text-sm text-mb-fg2 max-w-2xl">
          Wire AiSensy to your hospital so patients receive booking and payment messages over WhatsApp.
        </p>
      </div>

      {ok ? (
        <div data-testid="wa-status-banner" className="rounded-xl border border-teal-500/20 bg-teal-500/5 px-5 py-4 flex items-center gap-3">
          <CheckCircle2 className="h-5 w-5 text-teal-300" />
          <div className="text-sm text-mb-fg2">WhatsApp notifications are live for this hospital.</div>
        </div>
      ) : (
        <div data-testid="wa-status-banner" className="rounded-xl border border-amber-500/20 bg-amber-500/5 px-5 py-4 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-300 mt-0.5" />
          <div className="text-sm text-mb-fg2">
            <div className="font-medium text-amber-200">Demo mode</div>
            <div className="mt-1 text-mb-fg2">
              WhatsApp messages are being logged as MOCK + in-app notifications. Configure AiSensy below to go live.
            </div>
          </div>
        </div>
      )}

      <section className="rounded-xl border border-mb-border bg-mb-surface" data-testid="wa-card">
        <header className="px-6 py-5 border-b border-mb-border flex items-start gap-3">
          <div className="h-10 w-10 rounded-lg bg-teal-500/10 text-teal-300 flex items-center justify-center">
            <MessageCircle className="h-5 w-5" />
          </div>
          <div>
            <div className="text-base font-medium">AiSensy WhatsApp</div>
            <div className="text-xs text-mb-fg2 mt-1 max-w-xl">
              Create one AiSensy campaign per notification type with a pre-approved template, then paste the campaign names below. The campaign names — not the templates — are what the API needs.
            </div>
          </div>
        </header>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="API key">
            <div className="flex items-stretch gap-2">
              <Input
                type={showKey ? "text" : "password"}
                value={form.api_key}
                onChange={(e) => setForm({ ...form, api_key: e.target.value })}
                placeholder={data.api_key_masked ? `${data.api_key_masked} — leave blank to keep` : "Paste your AiSensy API key"}
                data-testid="wa-api-key"
                className="bg-mb-surface2 border-mb-border text-white h-10 rounded-lg flex-1" />
              <Button type="button" variant="outline" onClick={() => setShowKey((s) => !s)}
                className="bg-transparent border-mb-border hover:bg-mb-surface2 text-mb-fg2 h-10 w-10 p-0"
                data-testid="wa-api-key-toggle">
                {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
          </Field>
          <Field label="Enable WhatsApp">
            <div className="flex items-center gap-3 h-10">
              <Switch checked={form.is_enabled}
                onCheckedChange={(v) => setForm({ ...form, is_enabled: v })}
                data-testid="wa-enabled" />
              <span className="text-xs text-mb-fg2">{form.is_enabled ? "On" : "Off"}</span>
            </div>
          </Field>

          {CAMPAIGN_KEYS.map((c) => (
            <Field key={c.key} label={c.label}>
              <Input
                value={form.campaign_map[c.key] || ""}
                onChange={(e) => setForm({
                  ...form,
                  campaign_map: { ...form.campaign_map, [c.key]: e.target.value },
                })}
                placeholder={`AiSensy campaign name for "${c.label}"`}
                data-testid={`wa-campaign-${c.key}`}
                className="bg-mb-surface2 border-mb-border text-white h-10 rounded-lg" />
            </Field>
          ))}
        </div>
        <footer className="px-6 py-4 border-t border-mb-border flex items-center justify-end">
          <Button onClick={save} disabled={saving} data-testid="wa-save"
            className="bg-teal-500 hover:bg-teal-400 text-zinc-950">
            {saving ? "Saving…" : "Save WhatsApp"}
          </Button>
        </footer>
      </section>

      <section className="rounded-xl border border-mb-border bg-mb-surface" data-testid="wa-test-card">
        <header className="px-6 py-5 border-b border-mb-border">
          <div className="text-base font-medium inline-flex items-center gap-2"><SendHorizonal className="h-4 w-4 text-teal-300" /> Test send</div>
          <div className="text-xs text-mb-fg2 mt-1">Pick a notification type, paste a phone number, and we'll hit AiSensy with your stored key.</div>
        </header>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="Notification type">
            <Select value={test.campaign_name} onValueChange={(v) => setTest({ ...test, campaign_name: v })}>
              <SelectTrigger data-testid="wa-test-type" className="bg-mb-surface2 border-mb-border text-white h-10 rounded-lg">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-mb-surface2 border-mb-border text-white">
                {CAMPAIGN_KEYS.map((c) => (
                  <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Phone (E.164)">
            <Input value={test.to_phone}
              onChange={(e) => setTest({ ...test, to_phone: e.target.value })}
              placeholder="+911234567890"
              data-testid="wa-test-phone"
              className="bg-mb-surface2 border-mb-border text-white h-10 rounded-lg" />
          </Field>
          <Field label="Template param 1 (optional)">
            <Input value={test.param1} onChange={(e) => setTest({ ...test, param1: e.target.value })}
              data-testid="wa-test-param-1"
              className="bg-mb-surface2 border-mb-border text-white h-10 rounded-lg" />
          </Field>
          <Field label="Template param 2 (optional)">
            <Input value={test.param2} onChange={(e) => setTest({ ...test, param2: e.target.value })}
              data-testid="wa-test-param-2"
              className="bg-mb-surface2 border-mb-border text-white h-10 rounded-lg" />
          </Field>
        </div>
        <footer className="px-6 py-4 border-t border-mb-border flex justify-end">
          <Button onClick={runTest} disabled={testing || !data.is_enabled || !data.is_configured}
            data-testid="wa-test-send"
            className="bg-teal-500 hover:bg-teal-400 text-zinc-950">
            {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send test"}
          </Button>
        </footer>
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.2em] text-mb-fg2">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
